import { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { Users, Plus, PencilSimple, Trash, MagnifyingGlass, Upload, EnvelopeSimple, PaperPlaneTilt, X, UserPlus, WarningCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

const EMAIL_LABELS = ['Padre', 'Madre', 'Tutore', 'Alunno', 'Altro'];

// Ritorna true se la scadenza è oggi o passata (giorno UTC-independent)
const isExpired = (scadenza) => {
  if (!scadenza) return false;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(scadenza + 'T00:00:00');
  return d <= today;
};
const formatDateIt = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

export function PotentialStudentsSection() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showExpiredOnly, setShowExpiredOnly] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', strumento: '', note: '', scadenza: '', emails: [] });

  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showMassEmail, setShowMassEmail] = useState(false);
  const [massEmailForm, setMassEmailForm] = useState({ template_id: '' });
  const [sendingMass, setSendingMass] = useState(false);

  const [showImport, setShowImport] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);

  const [showSingleEmail, setShowSingleEmail] = useState(false);
  const [singleEmailStudent, setSingleEmailStudent] = useState(null);
  const [singleEmailForm, setSingleEmailForm] = useState({ template_id: '' });
  const [sendingSingle, setSendingSingle] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getPotentialStudents();
      setStudents(res.data);
    } catch { toast.error('Errore caricamento'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.getEmailTemplates().then(res => setEmailTemplates(res.data.filter(t => t.target_type === 'potenziali'))).catch(() => {});
  }, []);

  const openNew = () => { setEditing(null); setForm({ name: '', phone: '', strumento: '', note: '', scadenza: '', emails: [{ address: '', label: 'Padre' }] }); setShowModal(true); };
  const openEdit = (s) => { setEditing(s); setForm({ name: s.name, phone: s.phone || '', strumento: s.strumento || '', note: s.note || '', scadenza: s.scadenza || '', emails: s.emails?.length ? [...s.emails] : [] }); setShowModal(true); };

  const addEmail = () => setForm({ ...form, emails: [...form.emails, { address: '', label: 'Padre' }] });
  const removeEmail = (i) => setForm({ ...form, emails: form.emails.filter((_, idx) => idx !== i) });

  const handleSave = async (e) => {
    e.preventDefault();
    const cleanEmails = form.emails.filter(em => em.address.trim());
    try {
      if (editing) {
        await api.updatePotentialStudent(editing.id, { ...form, emails: cleanEmails });
        toast.success('Alunno potenziale aggiornato');
      } else {
        await api.createPotentialStudent({ ...form, emails: cleanEmails });
        toast.success('Alunno potenziale creato');
      }
      setShowModal(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo alunno potenziale?')) return;
    try { await api.deletePotentialStudent(id); toast.success('Eliminato'); load(); }
    catch { toast.error('Errore eliminazione'); }
  };

  const handleMakeEffective = async (s) => {
    if (!window.confirm(`Rendere ${s.name} alunno effettivo?`)) return;
    try {
      const res = await api.makePotentialEffective(s.id);
      toast.success(res.data.message);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true);
    try {
      const res = await api.importPotentialStudents(importFile);
      toast.success(`Importati ${res.data.created} alunni potenziali`);
      setShowImport(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore import'); }
    finally { setImporting(false); }
  };

  const filtered = students.filter(s => {
    if (search && !s.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (showExpiredOnly && !isExpired(s.scadenza)) return false;
    return true;
  });
  const expiredCount = students.filter(s => isExpired(s.scadenza)).length;
  const toggleSelect = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleAll = () => {
    const withEmail = filtered.filter(s => s.emails?.some(em => em.address));
    setSelectedIds(prev => prev.length === withEmail.length ? [] : withEmail.map(s => s.id));
  };

  const handleSendMassEmail = async () => {
    if (!massEmailForm.template_id) { toast.error('Seleziona un modello'); return; }
    const targets = students.filter(s => selectedIds.includes(s.id) && s.emails?.some(em => em.address));
    if (!targets.length) { toast.error('Nessun alunno con email'); return; }
    setSendingMass(true);
    let sent = 0, errors = 0;
    for (const s of targets) {
      const recipients = s.emails.filter(em => em.address).map(em => em.address);
      try { await api.sendEmailPotential({ student_id: s.id, template_id: massEmailForm.template_id, recipients }); sent++; }
      catch { errors++; }
    }
    setSendingMass(false); setShowMassEmail(false); setSelectedIds([]);
    if (errors === 0) toast.success(`Email inviate a ${sent} alunni potenziali`);
    else toast.warning(`Inviate ${sent}, errori ${errors}`);
  };

  const openSingleEmail = (s) => { setSingleEmailStudent(s); setSingleEmailForm({ template_id: '' }); setShowSingleEmail(true); };
  const handleSendSingleEmail = async () => {
    if (!singleEmailForm.template_id) { toast.error('Seleziona un modello'); return; }
    const recipients = (singleEmailStudent.emails || []).filter(em => em.address).map(em => em.address);
    if (!recipients.length) { toast.error('Nessuna email'); return; }
    setSendingSingle(true);
    try {
      await api.sendEmailPotential({ student_id: singleEmailStudent.id, template_id: singleEmailForm.template_id, recipients });
      toast.success(`Email inviata a ${singleEmailStudent.name}`);
      setShowSingleEmail(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore invio'); }
    finally { setSendingSingle(false); }
  };

  return (
    <>
      <div className="mb-6 animate-fade-in flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div><h1 className="text-2xl sm:text-3xl font-bold font-['Work_Sans'] tracking-tight mb-2">Note / Richieste</h1><p className="text-[#52525B] text-sm">Gestisci potenziali iscritti, richieste e appunti</p></div>
        <div className="flex gap-2">
          <Button onClick={() => { setImportFile(null); setShowImport(true); }} variant="outline" className="rounded-sm" data-testid="import-potential-btn"><Upload size={18} className="mr-2" />Importa</Button>
          <Button onClick={openNew} className="bg-[#0A0A0A] rounded-sm" data-testid="add-potential-btn"><Plus size={18} className="mr-2" />Nuovo</Button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3 mb-4 animate-fade-in animate-delay-1">
        <div className="relative"><MagnifyingGlass size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#A1A1AA]" /><Input type="text" placeholder="Cerca..." value={search} onChange={e => setSearch(e.target.value)} className="w-[200px] rounded-sm h-9 text-sm pl-8 uppercase" data-testid="search-potential" /></div>
        {expiredCount > 0 && (
          <button
            onClick={() => setShowExpiredOnly(v => !v)}
            className={`flex items-center gap-1.5 rounded-sm h-9 px-3 text-sm font-semibold transition-colors ${showExpiredOnly ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-red-50 text-red-700 border border-red-200 hover:bg-red-100'}`}
            data-testid="filter-expired-btn"
            title={showExpiredOnly ? 'Mostra tutte' : 'Filtra solo scadute'}
          >
            <WarningCircle size={16} weight="fill" />
            {expiredCount} scaduta{expiredCount === 1 ? '' : 'e'}
          </button>
        )}
        {selectedIds.length > 0 && (
          <Button onClick={() => { setMassEmailForm({ template_id: '' }); setShowMassEmail(true); }} className="bg-[#2D2A4A] hover:bg-[#1e1c38] rounded-sm h-9 text-sm" data-testid="mass-email-potential-btn">
            <EnvelopeSimple size={16} className="mr-1" />Invia Email ({selectedIds.length})
          </Button>
        )}
        <div className="bg-white border border-[#E4E4E7] rounded-sm px-4 py-1.5 text-sm ml-auto"><span className="text-[#52525B]">Totale:</span> <span className="font-bold text-lg font-['Work_Sans']">{filtered.length}</span></div>
      </div>

      <div className="animate-fade-in animate-delay-2 overflow-x-auto">
        {loading ? <div className="text-center py-12 text-[#52525B]">Caricamento...</div> : filtered.length === 0 ? (
          <div className="text-center py-16 bg-white border border-[#E4E4E7] rounded-sm"><Users size={48} className="mx-auto text-[#E4E4E7] mb-4" /><p className="text-[#52525B]">Nessuna nota / richiesta</p></div>
        ) : (
          <table className="data-table" data-testid="potential-students-table">
            <thead><tr>
              <th className="w-10"><Checkbox checked={selectedIds.length > 0 && selectedIds.length === filtered.filter(s => s.emails?.some(em => em.address)).length} onCheckedChange={toggleAll} /></th>
              <th>#</th><th>Nome</th><th>Telefono</th><th>Email</th><th>Strumento</th><th>Scadenza</th><th className="min-w-[280px]">Note</th><th className="text-right">Azioni</th>
            </tr></thead>
            <tbody>{filtered.map((s, i) => {
              const hasEmail = s.emails?.some(em => em.address);
              const expired = isExpired(s.scadenza);
              const rowClasses = [
                selectedIds.includes(s.id) ? 'bg-blue-50/50' : '',
                expired ? 'bg-red-50/60 border-l-4 border-l-red-500' : '',
              ].filter(Boolean).join(' ');
              return (<tr key={s.id} className={rowClasses}>
                <td className="w-10">{hasEmail ? <Checkbox checked={selectedIds.includes(s.id)} onCheckedChange={() => toggleSelect(s.id)} /> : <span className="block w-4" />}</td>
                <td className="text-sm text-[#A1A1AA]">{i + 1}</td>
                <td className="text-sm font-medium uppercase">{s.name}</td>
                <td className="text-sm text-[#52525B]">{s.phone || <span className="text-[#D4D4D8]">-</span>}</td>
                <td className="text-sm text-[#52525B]">{s.emails?.length > 0 ? s.emails.map((em, idx) => em.address ? <div key={idx} className="text-xs"><span className="text-[#A1A1AA]">{em.label}:</span> {em.address}</div> : null) : <span className="text-[#D4D4D8]">-</span>}</td>
                <td className="text-sm">{s.strumento || <span className="text-[#D4D4D8]">-</span>}</td>
                <td className="text-xs whitespace-nowrap">
                  {s.scadenza ? (
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded font-semibold ${expired ? 'bg-red-600 text-white' : 'bg-amber-50 text-amber-700 border border-amber-200'}`} title={expired ? 'Scaduta' : 'In scadenza'}>
                      {expired && <WarningCircle size={12} weight="fill" />}
                      {formatDateIt(s.scadenza)}
                    </span>
                  ) : <span className="text-[#D4D4D8]">-</span>}
                </td>
                <td className="text-sm text-[#52525B] max-w-[350px]">
                  <div className="whitespace-pre-wrap break-words leading-snug">{s.note || <span className="text-[#D4D4D8]">-</span>}</div>
                </td>
                <td className="text-right"><div className="flex items-center justify-end gap-1">
                  <button onClick={() => handleMakeEffective(s)} className="p-1.5 rounded hover:bg-green-50 text-green-600" title="Rendi Effettivo" data-testid={`make-effective-${s.id}`}><UserPlus size={16} /></button>
                  {hasEmail && <button onClick={() => openSingleEmail(s)} className="p-1.5 rounded hover:bg-blue-50 text-blue-600" title="Invia Email"><EnvelopeSimple size={16} /></button>}
                  <button onClick={() => openEdit(s)} className="p-1.5 rounded hover:bg-[#F4F4F5] text-[#52525B]"><PencilSimple size={16} /></button>
                  <button onClick={() => handleDelete(s.id)} className="p-1.5 rounded hover:bg-red-50 text-red-600"><Trash size={16} /></button>
                </div></td>
              </tr>);
            })}</tbody>
          </table>
        )}
      </div>

      {/* CRUD Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-['Work_Sans']">{editing ? 'Modifica' : 'Nuova'} Nota / Richiesta</DialogTitle><DialogDescription>{editing ? 'Modifica i dati' : 'Inserisci i dati'}</DialogDescription></DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-2"><Label className="label-overline">Cognome Nome</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required placeholder="Cognome Nome" className="rounded-sm uppercase" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label className="label-overline">Telefono</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="Telefono" className="rounded-sm" /></div>
              <div className="space-y-2"><Label className="label-overline">Scadenza</Label><Input type="date" value={form.scadenza} onChange={e => setForm({ ...form, scadenza: e.target.value })} className="rounded-sm" data-testid="scadenza-input" /></div>
            </div>
            <div className="space-y-2"><Label className="label-overline">Strumento di interesse</Label><Input value={form.strumento} onChange={e => setForm({ ...form, strumento: e.target.value })} placeholder="es. Pianoforte" className="rounded-sm" /></div>
            <div className="space-y-2"><Label className="label-overline">Note</Label><Textarea value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} placeholder="Scrivi note, richieste, appunti..." rows={6} className="rounded-sm resize-y min-h-[120px]" data-testid="note-textarea" /></div>
            <div className="space-y-2">
              <div className="flex items-center justify-between"><Label className="label-overline">Email</Label><Button type="button" variant="ghost" size="sm" onClick={addEmail} className="h-6 text-xs"><Plus size={12} className="mr-1" />Aggiungi</Button></div>
              {form.emails.map((em, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Select value={em.label} onValueChange={v => { const e2 = [...form.emails]; e2[i].label = v; setForm({ ...form, emails: e2 }); }}>
                    <SelectTrigger className="w-[100px] rounded-sm h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{EMAIL_LABELS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="email" value={em.address} onChange={e => { const e2 = [...form.emails]; e2[i].address = e.target.value; setForm({ ...form, emails: e2 }); }} placeholder="email@..." className="rounded-sm h-8 text-sm flex-1" />
                  <button type="button" onClick={() => removeEmail(i)} className="p-1 text-red-500 hover:bg-red-50 rounded"><X size={14} /></button>
                </div>
              ))}
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setShowModal(false)} className="rounded-sm">Annulla</Button><Button type="submit" className="bg-[#0A0A0A] rounded-sm">{editing ? 'Aggiorna' : 'Crea'}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import Modal */}
      <Dialog open={showImport} onOpenChange={setShowImport}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-['Work_Sans']">Importa Alunni Potenziali</DialogTitle><DialogDescription>Carica file Excel con colonne: Cognome Nome, Telefono, Email, Strumento</DialogDescription></DialogHeader>
          <Input type="file" accept=".xlsx,.xls" onChange={e => setImportFile(e.target.files?.[0])} className="rounded-sm" />
          <DialogFooter><Button variant="outline" onClick={() => setShowImport(false)} className="rounded-sm">Annulla</Button><Button onClick={handleImport} disabled={!importFile || importing} className="bg-[#0A0A0A] rounded-sm">{importing ? 'Importazione...' : 'Importa'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mass Email Modal */}
      <Dialog open={showMassEmail} onOpenChange={setShowMassEmail}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-['Work_Sans']">Invio Email di Massa</DialogTitle><DialogDescription>Invia a {selectedIds.length} alunni potenziali selezionati</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1"><Label className="label-overline">Modello Email</Label>
              <Select value={massEmailForm.template_id} onValueChange={v => setMassEmailForm({ template_id: v })}>
                <SelectTrigger className="rounded-sm"><SelectValue placeholder="Scegli modello..." /></SelectTrigger>
                <SelectContent>{emailTemplates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
              {emailTemplates.length === 0 && <p className="text-xs text-amber-600 mt-1">Nessun modello con categoria "Alunni Potenziali". Creane uno in Modelli Email.</p>}
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowMassEmail(false)} className="rounded-sm">Annulla</Button><Button onClick={handleSendMassEmail} disabled={sendingMass || !massEmailForm.template_id} className="bg-[#2D2A4A] hover:bg-[#1e1c38] rounded-sm">{sendingMass ? 'Invio...' : <><PaperPlaneTilt size={16} className="mr-1" />Invia</>}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Single Email Modal */}
      <Dialog open={showSingleEmail} onOpenChange={setShowSingleEmail}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-['Work_Sans']">Invia Email</DialogTitle><DialogDescription>Invia email a {singleEmailStudent?.name}</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1"><Label className="label-overline">Destinatari</Label>
              <div className="text-sm text-[#52525B]">{(singleEmailStudent?.emails || []).filter(em => em.address).map((em, i) => <div key={i} className="text-xs"><span className="text-[#A1A1AA]">{em.label}:</span> {em.address}</div>)}</div>
            </div>
            <div className="space-y-1"><Label className="label-overline">Modello Email</Label>
              <Select value={singleEmailForm.template_id} onValueChange={v => setSingleEmailForm({ template_id: v })}>
                <SelectTrigger className="rounded-sm"><SelectValue placeholder="Scegli modello..." /></SelectTrigger>
                <SelectContent>{emailTemplates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
              {emailTemplates.length === 0 && <p className="text-xs text-amber-600 mt-1">Nessun modello con categoria "Alunni Potenziali". Creane uno in Modelli Email.</p>}
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowSingleEmail(false)} className="rounded-sm">Annulla</Button><Button onClick={handleSendSingleEmail} disabled={sendingSingle || !singleEmailForm.template_id} className="bg-[#2D2A4A] hover:bg-[#1e1c38] rounded-sm">{sendingSingle ? 'Invio...' : <><PaperPlaneTilt size={16} className="mr-1" />Invia</>}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
